import {
  computed,
  shallowRef,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type ShallowRef,
} from 'vue'

import type { ReportDataState, ReportSpec } from '../models/report.types'

type VisibleReportState = Exclude<ReportDataState, 'idle'>
type SettledReportState = Extract<ReportDataState, 'ready' | 'empty'>

export interface SettledReportRenderSnapshot<T> {
  specKey: string
  state: SettledReportState
  value: T
}

/** One read, or the several a surface settles together (a `chart-table`'s table and chart). */
export type RenderSnapshotSpec = ReportSpec | readonly (ReportSpec | null)[] | null | undefined

export interface UseSettledReportRenderSnapshotOptions<T> {
  state: MaybeRefOrGetter<ReportDataState>
  currentSpec: MaybeRefOrGetter<RenderSnapshotSpec>
  settledSpec: MaybeRefOrGetter<RenderSnapshotSpec>
  capture: () => T
}

export interface UseSettledReportRenderSnapshot<T> {
  snapshot: ShallowRef<SettledReportRenderSnapshot<T> | null>
  value: ComputedRef<T>
  state: ComputedRef<VisibleReportState>
  holding: ComputedRef<boolean>
}

function specKey(value: RenderSnapshotSpec): string {
  return JSON.stringify(value ?? null)
}

/** Freeze one renderer payload until the exact spec that produced the response settles. */
export function useSettledReportRenderSnapshot<T>(
  options: UseSettledReportRenderSnapshotOptions<T>,
): UseSettledReportRenderSnapshot<T> {
  const snapshot = shallowRef<SettledReportRenderSnapshot<T> | null>(null)
  const liveValue = computed(options.capture)
  const currentKey = computed(() => specKey(toValue(options.currentSpec)))
  const settledKey = computed(() => specKey(toValue(options.settledSpec)))

  watch(
    () => ({
      state: toValue(options.state),
      currentKey: currentKey.value,
      settledKey: settledKey.value,
      value: liveValue.value,
    }),
    ({ state, currentKey: activeKey, settledKey: responseKey, value }) => {
      if ((state === 'ready' || state === 'empty') && activeKey === responseKey) {
        snapshot.value = { specKey: activeKey, state, value }
      }
    },
    { immediate: true, flush: 'sync' },
  )

  const active = computed(() => {
    const state = toValue(options.state)
    return (state === 'loading' || state === 'ready' || state === 'empty') && snapshot.value
      ? snapshot.value
      : null
  })

  return {
    snapshot,
    value: computed(() => active.value?.value ?? liveValue.value),
    state: computed(() => active.value?.state ?? normalizeState(toValue(options.state))),
    holding: computed(() => toValue(options.state) === 'loading' && active.value !== null),
  }
}

function normalizeState(state: ReportDataState): VisibleReportState {
  return state === 'idle' ? 'loading' : state
}
